import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useAlert } from "react-alert";
import { useApiError } from "../../hooks/useApiError.js";

import { SettingsContext } from "../../contexts";

import "./base-currency-panel.scss";

/**
 * Primary Admin → Base Currency.
 *
 * A store's own Primary Admin sets THEIR store's base currency; an RC master
 * admin can search any store and set it. Saves via the self-scoped endpoint
 * PATCH /api/store/base-currency — the backend forces a non-master to their
 * own store regardless of what the UI sends, so there is no cross-tenant write.
 *
 * The supported list comes from /api/app-settings (owner-accessible, seeded
 * into SettingsContext); it is only populated when multi-currency is enabled
 * server-side, so the panel shows a clear "not enabled" note otherwise.
 */
export function BaseCurrencyPanel({ user }) {
  const { settings, reloadSettings } = useContext(SettingsContext);
  const alert = useAlert();
  const { showApiError } = useApiError();
  const isMaster = !!user?.is_master_admin;

  const enabled = Array.isArray(settings?.currencies);
  const choices = (settings?.currencies || []).filter((c) => c.base_eligible);

  // Master: search + pick a store. Owner: implicit own store.
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null); // { id, name, base_currency }
  const [value, setValue] = useState(""); // '' === Default (GBP)
  const [saving, setSaving] = useState(false);

  // Owner: seed the current value from their own store's resolved base currency.
  useEffect(() => {
    if (!isMaster) {
      const cur = String(settings?.base_currency || "").toLowerCase();
      setValue(cur && cur !== "gbp" ? cur : "");
    }
  }, [isMaster, settings?.base_currency]);

  const searchStores = async (term) => {
    setQ(term);
    setSelected(null);
    if (!term || term.trim().length < 2) {
      setResults([]);
      return;
    }
    try {
      const { data } = await axios.get("/api/admin/stores/branding", { params: { q: term } });
      setResults(data.items || []);
    } catch (e) {
      alert.show("Could not search stores.");
    }
  };

  const pickStore = (s) => {
    setSelected(s);
    setValue(String(s.base_currency || "").toLowerCase());
    setResults([]);
    setQ(`${s.name || "Store"} (#${s.id})`);
  };

  // The store's currently-stored value, for change detection + the warning.
  const current = isMaster
    ? String(selected?.base_currency || "").toLowerCase()
    : String(settings?.base_currency || "").toLowerCase();
  const currentNorm = current && current !== "gbp" ? current : "";

  const save = async () => {
    if (value === currentNorm) {
      alert.show("No change to save.");
      return;
    }
    const label = isMaster ? (selected?.name || `store ${selected?.id}`) : "your store";
    const to = (value || "gbp").toUpperCase();
    const ok = window.confirm(
      `Change ${label} base currency to ${to}?\n\n` +
      `• Existing orders, payments and quotes keep their original currency — nothing is converted or rewritten.\n` +
      `• New listings, quotes and payments will default to ${to}.\n` +
      `• Reports will display in ${to} (converted at current rates).\n` +
      `• Monthly turnover/profit targets are NOT converted — re-enter them on the Store Performance page after saving.`
    );
    if (!ok) return;

    setSaving(true);
    try {
      await axios.patch("/api/store/base-currency", {
        base_currency: value || null,
        ...(isMaster && selected ? { store_id: selected.id } : {}),
      });
      alert.show("Base currency saved.");
      if (isMaster && selected) {
        setSelected({ ...selected, base_currency: value || null });
      } else if (typeof reloadSettings === "function") {
        // Own store: refresh settings so the seeded currency + UI update.
        reloadSettings();
      }
    } catch (e) {
      showApiError(e, "save the base currency");
    } finally {
      setSaving(false);
    }
  };

  if (!enabled) {
    return (
      <p className="ma-bc__hint">
        Multi-currency is not enabled on this environment, so the base currency
        can be viewed but not changed here yet.
      </p>
    );
  }

  const showSelect = !isMaster || !!selected;

  return (
    <div className="ma-bc">
      <p className="ma-bc__intro">
        Sets the default currency for new listings, quotes and payments, and the
        currency reports are shown in. Existing records keep their own currency.
      </p>

      {isMaster && (
        <div className="ma-bc__store">
          <label className="ma-bc__label">Store</label>
          <input
            type="text"
            value={q}
            placeholder="Search a store by name or id…"
            onChange={(e) => searchStores(e.target.value)}
          />
          {results.length > 0 && (
            <ul className="ma-bc__results">
              {results.map((s) => (
                <li key={s.id}>
                  <button type="button" onClick={() => pickStore(s)}>
                    {s.name || "Store"} (#{s.id})
                    {s.base_currency ? ` — ${String(s.base_currency).toUpperCase()}` : ""}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showSelect && (
        <div className="ma-bc__set">
          <label className="ma-bc__label">
            Base currency{isMaster && selected ? ` for ${selected.name || `store ${selected.id}`}` : ""}
          </label>
          <select value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">Default (GBP)</option>
            {choices.map((c) => (
              <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
            ))}
          </select>
          <button type="button" className="rc-btn" disabled={saving} onClick={save}>
            {saving ? "Saving…" : "Save base currency"}
          </button>
        </div>
      )}
    </div>
  );
}
